// components/employes/EmployesRadies.jsx
// ============================================================================
// EMPLOYÉS RADIÉS
// ============================================================================
// Vue ouverte depuis la liste des employés. Un employé radié garde tout son historique ;
// on peut le restaurer (accès et badge restent fermés), ou supprimer sa fiche
// si elle n'a aucune trace — c'est le serveur qui le vérifie et le dit.
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArchiveRestore, Loader2, RefreshCw, Trash2, UserMinus } from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';
import { radiationEmployesApi, usePermissionsRadiationEmployes } from '../../hooks/useRadiationEmployes';

const BOUTON_PETIT = 'inline-flex h-8 items-center justify-center gap-1.5 rounded-md border px-3 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50';

const formaterDate = (valeur) => {
  if (!valeur) return '-';
  const [annee, mois, jour] = String(valeur).slice(0, 10).split('-');
  return annee && mois && jour ? `${jour}/${mois}/${annee}` : '-';
};

const EmployesRadies = ({ onMessage, onChangement }) => {
  const { peutRestaurer, peutSupprimer } = usePermissionsRadiationEmployes();
  const [employes, setEmployes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [confirmation, setConfirmation] = useState(null);
  const [enCours, setEnCours] = useState(null);
  // Le parent recrée sa fonction de message à chaque rendu : lue par une
  // référence, elle ne relance pas le chargement en boucle.
  const messageRef = useRef(onMessage);
  messageRef.current = onMessage;
  // ⚠️ LE TROISIÈME ARGUMENT PASSE. Il était jeté ici et dans le pont du
  // parent, alors que la plomberie d'EmployeesList accepte déjà `long` et
  // arbitre : tout était prêt sauf ces deux passages d'arguments.
  const signaler = (message, type, options) => messageRef.current?.(message, type, options);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      setEmployes(await radiationEmployesApi.radies());
    } catch (error) {
      messageRef.current?.(error.message, 'error');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const executer = async () => {
    const { action, employe } = confirmation;
    setConfirmation(null);
    setEnCours(employe.id);
    try {
      if (action === 'restaurer') {
        const resultat = await radiationEmployesApi.restaurer(employe.id);
        // ⚠️ UNE CONSIGNE, PAS UNE CONFIRMATION. Le serveur répond « X est de
        // nouveau actif. Son accès et son badge restent fermés : rouvrez-les
        // depuis sa fiche si besoin. » — il y a quelque chose à FAIRE ensuite.
        // 101 caractères de gabarit plus le nom, donc sous le seuil des 130 :
        // ce texte passait en 3 secondes.
        signaler(resultat.message, 'success', { duration: 8000 });
      } else {
        await radiationEmployesApi.supprimer(employe.id);
        signaler(`La fiche de ${employe.prenom} ${employe.nom} a été supprimée.`, 'success');
      }
      onChangement?.();
      await charger();
    } catch (error) {
      signaler(error.message, 'error');
    } finally {
      setEnCours(null);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#c66d59] text-white">
            <UserMinus size={18} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Employés radiés</h2>
            <p className="text-sm text-gray-500">Leur paie, leurs avances, leurs caisses et leurs cours restent dans l'historique.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={charger}
          disabled={chargement}
          className={`${BOUTON_PETIT} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
        >
          {/*
            `RefreshCw` RESTE ici : c'est un bouton « Actualiser », la flèche
            circulaire y est juste. Ce qui change, c'est qu'elle ne TOURNE plus :
            une icône de sens « rafraîchir » qui se met à tourner, c'est le
            « chargement maison » signalé en recette. Le chargement, lui, est
            `Loader2` partout dans l'application.
          */}
          {chargement ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Actualiser
        </button>
      </div>

      {chargement && employes.length === 0 ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="animate-spin text-[#246990]" size={26} />
        </div>
      ) : employes.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-gray-500">Aucun employé radié.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-5 py-3 font-medium">Employé</th>
                <th className="px-5 py-3 font-medium">Poste</th>
                <th className="px-5 py-3 font-medium">Embauche</th>
                <th className="px-5 py-3 font-medium">Sortie</th>
                <th className="px-5 py-3 font-medium">Motif</th>
                <th className="px-5 py-3 font-medium">Radié par</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {employes.map((employe) => (
                <tr key={employe.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium text-gray-900">{employe.prenom} {employe.nom}</td>
                  <td className="px-5 py-3 text-gray-600">{employe.poste || '-'}</td>
                  <td className="px-5 py-3 text-gray-600">{formaterDate(employe.date_embauche)}</td>
                  <td className="px-5 py-3 text-gray-600">{formaterDate(employe.date_sortie)}</td>
                  <td className="px-5 py-3 text-gray-600">{employe.motif_sortie || '-'}</td>
                  <td className="px-5 py-3 text-gray-600">{employe.radie_par_nom || '-'}</td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-2">
                      {peutRestaurer && (
                        <button
                          type="button"
                          disabled={enCours === employe.id}
                          onClick={() => setConfirmation({ action: 'restaurer', employe })}
                          className={`${BOUTON_PETIT} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
                        >
                          {enCours === employe.id ? <Loader2 size={14} className="animate-spin" /> : <ArchiveRestore size={14} />}
                          Restaurer
                        </button>
                      )}
                      {peutSupprimer && (
                        <button
                          type="button"
                          disabled={enCours === employe.id}
                          onClick={() => setConfirmation({ action: 'supprimer', employe })}
                          className={`${BOUTON_PETIT} border-red-200 bg-white text-red-700 hover:bg-red-50`}
                          title="Réservé aux fiches créées par erreur, sans aucun historique"
                        >
                          <Trash2 size={14} />
                          Supprimer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(confirmation)}
        onClose={() => setConfirmation(null)}
        onConfirm={executer}
        title={confirmation?.action === 'restaurer' ? 'Restaurer cet employé ?' : 'Supprimer définitivement cette fiche ?'}
        description={confirmation?.action === 'restaurer'
          ? `${confirmation?.employe?.prenom} ${confirmation?.employe?.nom} redeviendra actif. Son accès à l'application et son badge restent fermés : rouvrez-les depuis sa fiche si besoin.`
          : `La fiche de ${confirmation?.employe?.prenom} ${confirmation?.employe?.nom} sera effacée. C'est possible uniquement si elle n'a aucun historique (paie, caisse, notes, cours, pointages).`}
        confirmText={confirmation?.action === 'restaurer' ? 'Restaurer' : 'Supprimer'}
        cancelText="Annuler"
        type={confirmation?.action === 'restaurer' ? 'question' : 'error'}
      />
    </div>
  );
};

export default EmployesRadies;
